import { push, replace } from 'redux-first-history';
import { all, call, put, takeEvery, takeLeading } from 'redux-saga/effects';

import GLOBAL_ERROR_MESSAGE from '@app/constants/errorMessages';
import {
  checkPasswordChangeFailure,
  checkPasswordChangeSuccess,
  findPassword,
  findPasswordFailure,
  findPasswordSuccess,
  postponePasswordChangeFailure,
  postponePasswordChangeSuccess,
  resetPassword,
  resetPasswordFailure,
  resetPasswordSuccess,
  validatePasswordToken,
  validatePasswordTokenFailure,
  validatePasswordTokenSuccess,
} from '@app/store/account/password/actions';
import {
  ACCOUNT_PASSWORD_CHANGE_CHECK_FAILURE,
  ACCOUNT_PASSWORD_CHANGE_CHECK_REQUEST,
  ACCOUNT_PASSWORD_CHANGE_CHECK_SUCCESS,
  ACCOUNT_PASSWORD_CHANGE_POSTPONE_FAILURE,
  ACCOUNT_PASSWORD_CHANGE_POSTPONE_REQUEST,
  ACCOUNT_PASSWORD_CHANGE_POSTPONE_SUCCESS,
  ACCOUNT_PASSWORD_FIND_FAILURE,
  ACCOUNT_PASSWORD_FIND_REQUEST,
  ACCOUNT_PASSWORD_FIND_SUCCESS,
  ACCOUNT_PASSWORD_RESET_FAILURE,
  ACCOUNT_PASSWORD_RESET_REQUEST,
  ACCOUNT_PASSWORD_RESET_SUCCESS,
  ACCOUNT_PASSWORD_TOKEN_VALIDATE_FAILURE,
  ACCOUNT_PASSWORD_TOKEN_VALIDATE_REQUEST,
} from '@app/store/actionTypes';
import { modalOpen } from '@app/store/modal/actions';
import {
  getUserPasswordChangeCheck,
  postUserPasswordChangePostpone,
} from '@app/store/transport-layer/identityaccess/user/password/change';
import { postUserPasswordReset } from '@app/store/transport-layer/identityaccess/user/password/reset';
import { postUserPasswordResetToken } from '@app/store/transport-layer/identityaccess/user/password/reset/token';
import { postUserPasswordResetTokenCheck } from '@app/store/transport-layer/identityaccess/user/password/reset/token/check';

export function* checkPasswordChangeSaga() {
  try {
    const isChanged: boolean = yield call(getUserPasswordChangeCheck);
    yield put(checkPasswordChangeSuccess({ isChanged }));
  } catch (error) {
    yield put(checkPasswordChangeFailure(error));
  }
}

export function* notifyPasswordChangeCheckSaga(
  action:
    | ReturnType<typeof checkPasswordChangeSuccess>
    | ReturnType<typeof checkPasswordChangeFailure>
) {
  switch (action.type) {
    case ACCOUNT_PASSWORD_CHANGE_CHECK_SUCCESS:
      // 비밀번호가 최근에 변경되지 않았다면 변경권장 모달을 표시한다.
      if (!action.payload.isChanged) {
        yield put(modalOpen('ACCOUNT_PASSWORD_CHANGE_RECOMMENDATION_MODAL'));
      }
      break;
    case ACCOUNT_PASSWORD_CHANGE_CHECK_FAILURE:
      yield put(
        modalOpen('CONFIRM', {
          content: GLOBAL_ERROR_MESSAGE[action.payload.message] || action.payload.message,
        })
      );
      break;
    default:
      break;
  }
}

export function* postponePasswordChangeSaga() {
  try {
    yield call(postUserPasswordChangePostpone);
    yield put(postponePasswordChangeSuccess());
  } catch (error) {
    yield put(postponePasswordChangeFailure(error));
  }
}

export function* notifyPasswordChangePostponeSaga(
  action:
    | ReturnType<typeof postponePasswordChangeSuccess>
    | ReturnType<typeof postponePasswordChangeFailure>
) {
  switch (action.type) {
    case ACCOUNT_PASSWORD_CHANGE_POSTPONE_SUCCESS:
      break;
    case ACCOUNT_PASSWORD_CHANGE_POSTPONE_FAILURE:
      yield put(
        modalOpen('CONFIRM', {
          content: GLOBAL_ERROR_MESSAGE[action.payload.message] || action.payload.message,
        })
      );
      break;
    default:
      break;
  }
}

export function* findPasswordSaga({ payload: email }: ReturnType<typeof findPassword>) {
  try {
    const data: { message: string } = yield call(postUserPasswordResetToken, email);
    yield put(findPasswordSuccess(data));
  } catch (error) {
    yield put(findPasswordFailure(error));
  }
}

const PASSWORD_FIND_RESPONSE: { [key: string]: string } = {
  'email user not found': '해당 유저를 찾을 수 없습니다.',
  'password reset requested': '비밀번호 재설정 이메일이 전송되었습니다.',
};

export function* notifyPasswordFindSaga({
  payload: { message },
}: ReturnType<typeof findPasswordSuccess> | ReturnType<typeof findPasswordFailure>) {
  yield put(
    modalOpen('CONFIRM', {
      content: PASSWORD_FIND_RESPONSE[message] || GLOBAL_ERROR_MESSAGE[message] || message,
    })
  );
}

export function* resetPasswordSaga({
  payload: { email, token, password },
}: ReturnType<typeof resetPassword>) {
  try {
    const data: Awaited<ReturnType<typeof postUserPasswordReset>> = yield call(
      postUserPasswordReset,
      { email, token, password }
    );
    yield put(resetPasswordSuccess(data));
  } catch (error) {
    yield put(resetPasswordFailure(error));
  }
}

const RESET_PASSWORD_RESPONSE: { [key: string]: string } = {
  'email user not found': '존재하지 않는 유저입니다.',
  'Invalid password reset token': '올바르지 않은 접근입니다.',
  'password reset': '비밀번호 재설정이 완료되었습니다.\n로그인해 주세요.',
};

export function* notifyPasswordResetSaga({
  type,
  payload: { message },
}: ReturnType<typeof resetPasswordSuccess> | ReturnType<typeof resetPasswordFailure>) {
  yield put(
    modalOpen('CONFIRM', {
      content: RESET_PASSWORD_RESPONSE[message] || GLOBAL_ERROR_MESSAGE[message] || message,
    })
  );

  if (type === ACCOUNT_PASSWORD_RESET_SUCCESS) {
    yield put(push('/authentication/signin'));
  }
}

export function* validatePasswordTokenSaga({
  payload: { email, token },
}: ReturnType<typeof validatePasswordToken>) {
  try {
    const isPasswordResetTokenValid: boolean = yield call(postUserPasswordResetTokenCheck, {
      email,
      token,
    });

    if (!isPasswordResetTokenValid) {
      throw new Error('Invalid access');
    }
    yield put(validatePasswordTokenSuccess());
  } catch (error) {
    yield put(validatePasswordTokenFailure(error));
  }
}

export function* notifyPasswordTokenValidationSaga() {
  yield put(replace('/password/reset/invalid'));
}

export default function* saga() {
  yield all([
    takeLeading(ACCOUNT_PASSWORD_CHANGE_CHECK_REQUEST, checkPasswordChangeSaga),
    takeEvery(
      [ACCOUNT_PASSWORD_CHANGE_CHECK_SUCCESS, ACCOUNT_PASSWORD_CHANGE_CHECK_FAILURE],
      notifyPasswordChangeCheckSaga
    ),
    takeLeading(ACCOUNT_PASSWORD_CHANGE_POSTPONE_REQUEST, postponePasswordChangeSaga),
    takeEvery(
      [ACCOUNT_PASSWORD_CHANGE_POSTPONE_SUCCESS, ACCOUNT_PASSWORD_CHANGE_POSTPONE_FAILURE],
      notifyPasswordChangePostponeSaga
    ),
    takeLeading(ACCOUNT_PASSWORD_FIND_REQUEST, findPasswordSaga),
    takeEvery(
      [ACCOUNT_PASSWORD_FIND_SUCCESS, ACCOUNT_PASSWORD_FIND_FAILURE],
      notifyPasswordFindSaga
    ),
    takeLeading(ACCOUNT_PASSWORD_RESET_REQUEST, resetPasswordSaga),
    takeEvery(
      [ACCOUNT_PASSWORD_RESET_SUCCESS, ACCOUNT_PASSWORD_RESET_FAILURE],
      notifyPasswordResetSaga
    ),
    takeLeading(ACCOUNT_PASSWORD_TOKEN_VALIDATE_REQUEST, validatePasswordTokenSaga),
    takeEvery([ACCOUNT_PASSWORD_TOKEN_VALIDATE_FAILURE], notifyPasswordTokenValidationSaga),
  ]);
}
